import {
  EnvelopeIcon,
  FilmStripIcon,
  GlobeHemisphereWestIcon,
} from '@phosphor-icons/react';
import { MembershipStatusEnum, Workspace } from '@sr-sdks/api-sdk-axios';
import {
  Button,
  type ButtonProps,
  Checkbox,
  Dialog,
  Icon,
  sonner,
} from '@sr/ui-library';
import { useEffect, useRef, useState } from 'react';
import { useUpdateWorkspaceDetails } from '../api/workspaces/workspaceApi';
import { MEMBERSHIP_AGREEMENT_URL } from '../constant/Common';
import { FEATURE_FLAGS } from '../constant/featureFlags';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

const { toast } = sonner;

type RequestMembershipModalProps = {
  isForced?: boolean;
  onSuccess?: () => void;
  /**
   * Restyles the trigger button, for callers where it is the primary action.
   * Presentation only: the trigger's behaviour is this component's to own, so
   * `onClick`, `children` and `asChild` are deliberately not accepted.
   */
  triggerButtonProps?: Pick<ButtonProps, 'className' | 'size' | 'variant'>;
  /** For a trigger standing in for another action, e.g. a task card's View. */
  triggerLabel?: string;
  workspace?: Workspace;
};

export const RequestMembershipModal = ({
  isForced = false,
  onSuccess,
  triggerButtonProps,
  triggerLabel = 'Apply for Membership',
  workspace,
}: RequestMembershipModalProps) => {
  const [isModalOpen, setIsModalOpen] = useState(isForced);
  const [isTermsOfServiceChecked, setIsTermsOfServiceChecked] =
    useState<boolean>(false);
  // The dialog has no Radix trigger, so Radix has nowhere to return focus to.
  const triggerRef = useRef<HTMLButtonElement>(null);

  const updateWorkspaceDetailsMutation = useUpdateWorkspaceDetails();
  const [isMsr3Enabled, featuresQuery] = useFeatureFlag(
    FEATURE_FLAGS.MSR3_ENABLED,
  );

  const isSubmitting = updateWorkspaceDetailsMutation.isPending;
  // Defer the trigger button render until feature flags resolve so v3 users
  // do not see the variant flip from primary to outline on cold load.
  const isApplyButtonVisible = !isForced && !featuresQuery.isLoading;
  const applyButtonVariant = isMsr3Enabled ? 'outline' : undefined;

  useEffect(() => {
    if (isForced) {
      setIsModalOpen(true);
    }
  }, [isForced]);

  const handleApplyMemberClick = async () => {
    if (!workspace?._id) return;

    try {
      await updateWorkspaceDetailsMutation.mutateAsync({
        id: workspace._id,
        updateWorkspaceDetailsDto: {
          membershipStatus: MembershipStatusEnum.InReview,
        },
      });

      setIsModalOpen(false);
      setIsTermsOfServiceChecked(false);
      if (onSuccess) onSuccess();
      toast.success('Membership application submitted successfully.');
    } catch (error) {
      toast.error('Failed to submit membership application');
    }
  };

  const handleCloseModal = () => {
    if (isForced) return;

    setIsModalOpen(false);
    setIsTermsOfServiceChecked(false);
  };

  return (
    <>
      {isApplyButtonVisible && (
        <Button
          size="sm"
          variant={applyButtonVariant}
          {...triggerButtonProps}
          ref={triggerRef}
          onClick={() => setIsModalOpen(true)}
        >
          {triggerLabel}
        </Button>
      )}

      <Dialog
        heading="Request membership"
        actionButtonProps={{
          children: 'Submit Membership',
          className: 'w-full sm:w-52',
          disabled: !isTermsOfServiceChecked || isSubmitting,
          loading: isSubmitting,
          onClick: handleApplyMemberClick,
        }}
        cancelButtonProps={
          isForced
            ? { className: 'hidden' }
            : {
                children: 'Cancel',
                className: 'w-full sm:w-52',
                disabled: isSubmitting,
                onClick: handleCloseModal,
                variant: 'outline',
              }
        }
        className={`max-h-[90vh] max-w-4xl overflow-y-auto p-4 sm:p-16 ${
          isForced ? '[&_button.absolute]:hidden' : ''
        }`}
        open={isModalOpen}
        showHeading={false}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          triggerRef.current?.focus();
        }}
        onOpenChange={(open) => {
          if (isForced || isSubmitting) return;

          setIsModalOpen(open);
          if (!open) setIsTermsOfServiceChecked(false);
        }}
      >
        <div className="flex flex-col gap-9">
          <header className="flex flex-col items-center gap-4 sm:items-start">
            <div className="rounded-3xl bg-sr-text-brand px-3.5 py-1">
              <div className="text-2xl font-bold text-sr-white">
                screenrights
              </div>
            </div>

            <p className="text-center text-xl text-sr-black sm:text-left">
              Screenrights membership is required for{' '}
              <strong>{workspace?.workspaceName}</strong> to collect secondary
              royalties.
            </p>
          </header>

          <div className="space-y-4 sm:space-y-5">
            <div className="rounded-lg border border-neutral-100 bg-neutral-100 p-3 shadow-sm sm:p-4">
              <h3 className="mb-4 text-sm text-sr-black sm:text-base">
                Maximise your earnings and resources with Screenrights. As a
                member you can:
              </h3>

              <ul className="space-y-2 text-neutral-600 sm:space-y-3">
                <li className="flex items-center gap-2">
                  <Icon
                    name="file-text"
                    className="text-sr-text-brand"
                    icon={FilmStripIcon}
                  />
                  <span className="text-sm">
                    Effortlessly register your titles for prompt royalty
                    payments.
                  </span>
                </li>

                <li className="flex items-center gap-2">
                  <Icon
                    name="users"
                    className="text-sr-text-brand"
                    icon={GlobeHemisphereWestIcon}
                  />
                  <span className="text-sm">
                    Leverage our global network to collect international
                    royalties.
                  </span>
                </li>

                <li className="flex items-center gap-2">
                  <Icon
                    name="calendar"
                    className="text-sr-text-brand"
                    icon="calendar"
                  />
                  <span className="text-sm">
                    Access free meeting rooms in our Sydney office.
                  </span>
                </li>
              </ul>
            </div>

            <div className="rounded-lg border border-neutral-100 bg-neutral-100 p-3 shadow-sm sm:p-4">
              <h3 className="mb-4 text-sm text-sr-black sm:mb-5 sm:text-base">
                After you submit your Screenrights membership application:
              </h3>

              <ul className="space-y-2 text-neutral-600 sm:space-y-3">
                <li className="flex gap-2">
                  <Icon
                    name="file-text"
                    className="text-sr-text-brand"
                    icon="edit"
                    size="lg"
                  />
                  <span className="text-sm">
                    Our team will review your workspace details and Membership
                    Agreement for compliance (typical turnaround time: 1-2
                    business days).
                  </span>
                </li>

                <li className="flex items-center gap-2">
                  <Icon
                    name="users"
                    className="text-sr-text-brand"
                    icon={EnvelopeIcon}
                  />
                  <span className="text-sm">
                    Once approved, you will receive a confirmation email with
                    instructions on how to register your titles.
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <label className="flex cursor-pointer items-center justify-end gap-3">
            <Checkbox
              checked={isTermsOfServiceChecked}
              className="mt-0.5 flex-shrink-0"
              disabled={isSubmitting}
              onCheckedChange={(checked) =>
                setIsTermsOfServiceChecked(checked === true)
              }
            />
            <span className="text-sm">
              I have read and agree to the{' '}
              <a
                className="text-sr-text-primary"
                href={MEMBERSHIP_AGREEMENT_URL}
                rel="noopener noreferrer"
                target="_blank"
              >
                Screenrights Membership Agreement
              </a>
              .
            </span>
          </label>
        </div>
      </Dialog>
    </>
  );
};
